/**
 * /workspaces/:slug/invites + /invites/:token endpoints.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@clarix/api-client';
import type {
  CreateInviteRequest,
  InviteRecord,
  InvitePreview,
  AcceptAndSignupRequest,
  AcceptAndSignupResponse,
  AcceptOtpAccountRequest,
  AcceptOtpAccountResponse,
} from '@clarix/shared-types';
import { unwrap, type ApiResponse } from './envelope';
import { queryKeys } from './keys';

/** POST /workspaces/:slug/invites */
export function useCreateInvite(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateInviteRequest): Promise<InviteRecord> => {
      const res = await getApiClient().post<ApiResponse<InviteRecord>>(
        `/workspaces/${slug}/invites`,
        body,
      );
      return unwrap(res.data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.workspaceInvites(slug) }),
  });
}

/** GET /workspaces/:slug/invites */
export function useWorkspaceInvites(slug: string | null) {
  return useQuery({
    queryKey: slug ? queryKeys.workspaceInvites(slug) : ['workspace', '__nil__', 'invites'],
    queryFn: async (): Promise<InviteRecord[]> => {
      const res = await getApiClient().get<ApiResponse<InviteRecord[]>>(
        `/workspaces/${slug}/invites`,
      );
      return unwrap(res.data);
    },
    enabled: !!slug,
  });
}

/** DELETE /workspaces/:slug/invites/:id */
export function useRevokeInvite(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      await getApiClient().delete(`/workspaces/${slug}/invites/${inviteId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.workspaceInvites(slug) }),
  });
}

/** GET /invites/:token — PUBLIC preview for the invite landing page. */
export function useInvitePreview(token: string | null) {
  return useQuery({
    queryKey: token ? queryKeys.invitePreview(token) : ['invite', '__nil__'],
    queryFn: async (): Promise<InvitePreview> => {
      const res = await getApiClient().get<ApiResponse<InvitePreview>>(`/invites/${token}`);
      return unwrap(res.data);
    },
    enabled: !!token,
    retry: false,
  });
}

/** POST /invites/:token/accept */
export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (token: string) => {
      const res = await getApiClient().post<ApiResponse<unknown>>(`/invites/${token}/accept`);
      return unwrap(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.me() });
      qc.invalidateQueries({ queryKey: queryKeys.myWorkspaces() });
    },
  });
}

/**
 * POST /invites/:token/accept-otp-account — provisions an OTP-only account
 * (no password) + membership from the invite token. Returns the target
 * workspace; the member then logs in via OTP on that subdomain. Public.
 */
export function useAcceptOtpAccount(token: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AcceptOtpAccountRequest): Promise<AcceptOtpAccountResponse> => {
      const res = await getApiClient().post<ApiResponse<AcceptOtpAccountResponse>>(
        `/invites/${token}/accept-otp-account`,
        body,
      );
      return unwrap(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.me() });
      qc.invalidateQueries({ queryKey: queryKeys.myWorkspaces() });
    },
  });
}

/** POST /invites/:token/decline */
export function useDeclineInvite() {
  return useMutation({
    mutationFn: async (token: string) => {
      const res = await getApiClient().post<ApiResponse<unknown>>(`/invites/${token}/decline`);
      return unwrap(res.data);
    },
  });
}

/**
 * POST /invites/:token/accept-and-signup — PUBLIC.
 * Invited user without an account: provides name + password; backend creates
 * the user, accepts the invite, and returns a JWT bound to the workspace.
 */
export function useAcceptAndSignup(token: string | null) {
  return useMutation({
    mutationFn: async (body: AcceptAndSignupRequest): Promise<AcceptAndSignupResponse> => {
      const res = await getApiClient().post<ApiResponse<AcceptAndSignupResponse>>(
        `/invites/${token}/accept-and-signup`,
        body,
      );
      return unwrap(res.data);
    },
  });
}
